import React, { useState, useEffect } from 'react';
import { getProducts, deleteProduct, toggleProductStatus } from '../../services/api';
import { getContents } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductForm from '../forms/ProductForm';
import ProductDetails from '../components/ProductDetails';
import toast from 'react-hot-toast';

const ProductsModule = () => {
  const [products, setProducts] = useState([]);
  const [productsContent, setProductsContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const productsResponse = await getProducts();
      let productsData = [];
      if (productsResponse && productsResponse.data && Array.isArray(productsResponse.data)) {
        productsData = productsResponse.data;
      } else if (productsResponse && Array.isArray(productsResponse)) {
        productsData = productsResponse;
      } else if (productsResponse && productsResponse.success && productsResponse.data && Array.isArray(productsResponse.data)) {
        productsData = productsResponse.data;
      }
      setProducts(productsData);
      
      const contentsResponse = await getContents();
      let allContents = [];
      if (contentsResponse && contentsResponse.data && Array.isArray(contentsResponse.data)) {
        allContents = contentsResponse.data;
      } else if (contentsResponse && Array.isArray(contentsResponse)) {
        allContents = contentsResponse;
      }
      
      const productsSection = allContents.find(c => c.sectionId === 'products');
      setProductsContent(productsSection || null);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load products');
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        toast.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        toast.error('Error deleting product');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleProductStatus(id);
      toast.success(`Product ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchProducts();
    } catch (error) {
      toast.error('Error toggling product status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleViewDetails = (item) => {
    setSelectedProduct(item);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedProduct(null);
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `http://localhost:5000${url}`;
    return url;
  };

  const getStatusBadge = (isActive) => {
    return {
      bg: isActive ? '#D4EDDA' : '#F8D7DA',
      color: isActive ? '#155724' : '#721C24',
      text: isActive ? 'Active' : 'Inactive'
    };
  };

  const backgroundOptions = [
    { name: 'Dark Blue Gradient', value: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)', textColor: 'white' },
    { name: 'Teal Gradient', value: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)', textColor: 'white' },
    { name: 'Purple Gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textColor: 'white' },
    { name: 'Orange Gradient', value: 'linear-gradient(135deg, #F5A623 0%, #F7B84D 100%)', textColor: 'white' },
    { name: 'White', value: '#FFFFFF', textColor: '#0A192F' },
    { name: 'Light Gray', value: '#F7F9FC', textColor: '#0A192F' },
    { name: 'Dark Blue Solid', value: '#0A192F', textColor: 'white' },
    { name: 'Teal Solid', value: '#2C7A7B', textColor: 'white' }
  ];

  const currentBg = productsContent?.backgroundColor || 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)';
  const currentTextColor = backgroundOptions.find(opt => opt.value === currentBg)?.textColor || 'white';

  if (loading) return <LoadingSpinner message="Loading products..." />;
  
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', background: '#FEE2E2', borderRadius: '12px' }}>
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#DC2626', marginBottom: '1rem' }}></i>
        <h3 style={{ color: '#DC2626', marginBottom: '0.5rem' }}>Error Loading Products</h3>
        <p style={{ color: '#5A6E7F', marginBottom: '1rem' }}>{error}</p>
        <button onClick={fetchProducts} style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          <i className="fas fa-sync-alt"></i> Try Again
        </button>
      </div>
    );
  }

  const headerTitle = productsContent?.title || 'Flagship Solutions';
  const headerSubtitle = productsContent?.subtitle || 'Industry-leading software platforms for engineering excellence.';
  const headerDescription = productsContent?.content || '';

  return (
    <div>
      {/* Header Section */}
      <div style={{ 
        background: currentBg,
        padding: '4rem 2rem',
        borderRadius: '20px',
        marginBottom: '2.5rem',
        color: currentTextColor,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          right: '-50%',
          bottom: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          animation: 'rotate 20s linear infinite',
          opacity: 0.3
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.9 }}>
            <i className="fas fa-cubes"></i>
          </div>
          
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)', 
            marginBottom: '1rem', 
            fontWeight: 800,
            letterSpacing: '-0.02em'
          }}>
            {headerTitle}
          </h1>
          
          <div style={{
            width: '80px',
            height: '4px',
            background: currentTextColor === 'white' ? 'rgba(255,255,255,0.3)' : '#2C7A7B',
            margin: '1rem auto',
            borderRadius: '2px'
          }} />
          
          <p style={{ 
            fontSize: 'clamp(1rem, 3.5vw, 1.3rem)', 
            opacity: 0.95,
            maxWidth: '800px', 
            margin: '0 auto',
            lineHeight: '1.6',
            fontWeight: 500
          }}>
            {headerSubtitle}
          </p>
          
          {headerDescription && (
            <p style={{ 
              fontSize: '1rem',
              opacity: 0.85,
              maxWidth: '600px',
              margin: '1rem auto 0',
              lineHeight: '1.6'
            }}>
              {headerDescription}
            </p>
          )}
        </div>
      </div>

      {/* Controls Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem', 
        flexWrap: 'wrap', 
        gap: '1rem' 
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '0.6rem 1.2rem',
              background: viewMode === 'grid' ? '#2C7A7B' : '#F7F9FC',
              color: viewMode === 'grid' ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500,
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-th-large"></i> Grid View
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '0.6rem 1.2rem',
              background: viewMode === 'table' ? '#2C7A7B' : '#F7F9FC',
              color: viewMode === 'table' ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500
            }}
          >
            <i className="fas fa-table"></i> Table View
          </button>
        </div>
        
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ 
            padding: '0.6rem 1.2rem', 
            background: '#2C7A7B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontWeight: 500,
            transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
        >
          <i className="fas fa-plus"></i> Add New Product
        </button>
      </div>

      {/* Products Count */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#5A6E7F', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-chart-line" style={{ color: '#2C7A7B' }}></i>
          <strong>{products.length}</strong> {products.length === 1 ? 'product' : 'products'} available
        </p>
      </div>

      {/* Products Display */}
      {products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '20px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-box-open" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No products found</p>
          <button onClick={() => { setEditingItem(null); setShowModal(true); }} style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>
            <i className="fas fa-plus"></i> Create Your First Product
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: '2rem'
        }}>
          {products.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              getFullImageUrl={getFullImageUrl}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Image</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Price</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Stock</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const status = getStatusBadge(product.isActive);
                return (
                  <tr key={product._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '1rem' }}>
                      {product.imageUrl ? (
                        <img 
                          src={getFullImageUrl(product.imageUrl)} 
                          alt={product.name} 
                          style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} 
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '50px', height: '50px', background: '#F7F9FC', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="fas fa-image" style={{ color: '#CBD5E1' }} />
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: 500 }}>{product.name}</td>
                    <td style={{ padding: '1rem' }}><span style={{ background: '#E3F2FD', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem' }}>{product.category}</span></td>
                    <td style={{ padding: '1rem', fontWeight: 600, color: '#2C7A7B' }}>{product.price}</td>
                    <td style={{ padding: '1rem' }}>{product.stock} units</td>
                    <td style={{ padding: '1rem' }}>
                      <button onClick={() => handleToggle(product._id, product.isActive)} style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', border: 'none', background: status.bg, color: status.color, cursor: 'pointer', fontSize: '0.75rem' }}>
                        {status.text}
                      </button>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <button onClick={() => handleViewDetails(product)} style={{ padding: '0.35rem 0.8rem', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '0.75rem' }}>
                        <i className="fas fa-eye"></i> View
                      </button>
                      <button onClick={() => handleEdit(product)} style={{ padding: '0.35rem 0.8rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem', fontSize: '0.75rem' }}>
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button onClick={() => handleDelete(product._id)} style={{ padding: '0.35rem 0.8rem', background: '#DC2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ProductForm 
          editingItem={editingItem} 
          onClose={handleCloseModal} 
          onRefresh={fetchProducts} 
        />
      )}
      
      {showDetailsModal && selectedProduct && (
        <ProductDetails 
          product={selectedProduct} 
          onClose={handleCloseDetails} 
          onEdit={() => {
            handleCloseDetails();
            handleEdit(selectedProduct);
          }}
          onRefresh={fetchProducts}
        />
      )}

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .product-card {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

// Product Card Component - FIXED with text buttons
const ProductCard = ({ product, getFullImageUrl, onViewDetails, onEdit, onDelete, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="product-card"
      style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        border: '1px solid #E2E8F0',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Status Badge */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        zIndex: 1
      }}>
        <button
          onClick={() => onToggle(product._id, product.isActive)}
          style={{
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            border: 'none',
            background: product.isActive ? '#10B981' : '#EF4444',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.7rem',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          {product.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {/* Product Image */}
      <div style={{
        height: '220px',
        background: 'linear-gradient(135deg, #F7F9FC 0%, #E2E8F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {product.imageUrl ? (
          <img
            src={getFullImageUrl(product.imageUrl)}
            alt={product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.5s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <i className="fas fa-cube" style={{ fontSize: '4rem', color: '#CBD5E1' }}></i>
            <p style={{ color: '#94A3B8', marginTop: '0.5rem', fontSize: '0.8rem' }}>No Image</p>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div style={{ padding: '1.5rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{
            background: '#E3F2FD',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.7rem',
            color: '#2C7A7B',
            fontWeight: 600,
            display: 'inline-block'
          }}>
            {product.category}
          </span>
        </div>

        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#0A192F',
          marginBottom: '0.5rem',
          lineHeight: '1.3'
        }}>
          {product.name}
        </h3>

        <p style={{
          fontSize: '0.85rem',
          color: '#5A6E7F',
          lineHeight: '1.5',
          marginBottom: '1rem',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '2.5rem'
        }}>
          {product.description || 'No description available'}
        </p>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <div>
            <span style={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Price</span>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2C7A7B' }}>{product.price}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stock</span>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0A192F' }}>{product.stock} units</div>
          </div>
        </div>

        {product.features && product.features.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {product.features.slice(0, 2).map((feature, idx) => (
                <span key={idx} style={{
                  fontSize: '0.7rem',
                  padding: '0.2rem 0.5rem',
                  background: '#F7F9FC',
                  borderRadius: '4px',
                  color: '#5A6E7F',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}>
                  <i className="fas fa-check-circle" style={{ color: '#10B981', fontSize: '0.6rem' }}></i>
                  {feature.length > 25 ? feature.substring(0, 25) + '...' : feature}
                </span>
              ))}
              {product.features.length > 2 && (
                <span style={{
                  fontSize: '0.7rem',
                  padding: '0.2rem 0.5rem',
                  background: '#F7F9FC',
                  borderRadius: '4px',
                  color: '#5A6E7F'
                }}>
                  +{product.features.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons - FIXED with text */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <button
            onClick={() => onViewDetails(product)}
            style={{
              flex: 1,
              padding: '0.6rem 0.8rem',
              background: '#2C7A7B',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
          >
            <i className="fas fa-eye"></i> View Details
          </button>
          <button
            onClick={() => onEdit(product)}
            style={{
              flex: 1,
              padding: '0.6rem 0.8rem',
              background: '#F5A623',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#E67E22'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F5A623'}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            onClick={() => onDelete(product._id)}
            style={{
              flex: 1,
              padding: '0.6rem 0.8rem',
              background: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#DC2626'}
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductsModule;